import { FileType, GDTMessageGroup, QueryFactory } from '@lmt/shared-types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';

import styles from './SendViaGDT.module.scss';
import {
  defaultExcludedFileTypes,
  getDocumentExtension,
} from '../documents/common';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { CheckboxGroup } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckboxGroup';
import { useEventCallback } from '@mui/material';

interface DocumentInfo {
  id: string;
  name: string;
  type: FileType;
  isSelected: boolean;
}

type SendViaGDTProps = FormComponentProps & {
  blockId: number;
};

const filteredFileTypes = [
  FileType.DeviceReport,
  FileType.External,
  FileType.Document,
];

export const SendViaGDT: React.FC<SendViaGDTProps> = (props) => {
  const { formType, formId, tryCompleteForm, blockId } = props;
  const queryClient = useQueryClient();
  const i18next = useScopedTranslation('patients.cases.send_via_gdt');
  const i18nextNotifications = useScopedTranslation('patients.notifications');
  const i18nextGlobal = useTranslation();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const [selectedDocumentTemplates, setSelectedDocumentTemplates] = useState<
    number[]
  >([]);
  const [selectedDataBlockTemplates, setSelectedDataBlockTemplates] = useState<
    number[]
  >([]);
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [isSending, setSending] = useState<boolean>(false);

  const { data: dataBlock } =
    QueryFactory.DataBlockQuery.useGetDataBlockQuery(blockId);
  const { data: patient } = QueryFactory.PatientQuery.useGetPatientQuery(
    dataBlock!.patientId!,
    {
      enabled: !!dataBlock?.patientId,
    },
  );
  const { data: gdtTemplates } =
    QueryFactory.GDTOutgoingTemplatesQuery.useGetQuery();
  const { data: gdtDataBlockTemplateRules } =
    QueryFactory.GDTTemplateRulesQuery.useGetQuery(
      {
        subtenantId: patient?.subtenantId,
        isActive: true,
        messageGroup: GDTMessageGroup.DataBlock,
        dataBlockTypeId: dataBlock!.typeId,
      },
      {
        enabled: !!dataBlock && !!patient,
      },
    );
  const { data: gdtDocumentTemplateRules } =
    QueryFactory.GDTTemplateRulesQuery.useGetQuery(
      {
        subtenantId: patient?.subtenantId,
        isActive: true,
        messageGroup: GDTMessageGroup.Document,
      },
      {
        enabled: !!patient,
      },
    );

  const dataBlockTemplateOptions = useMemo(() => {
    const templateIds = (gdtDataBlockTemplateRules ?? []).map(
      (rule) => rule.documentTemplateId,
    );
    const templates = (gdtTemplates ?? [])
      .filter((t) => templateIds.includes(t.id))
      .map((x) => {
        return {
          id: x.id,
          value: x.isDefault ? i18nextGlobal.t(x.title as any) : x.title,
        };
      });
    setSelectedDataBlockTemplates(templates.map((t) => t.id));
    return templates;
  }, [gdtTemplates, gdtDataBlockTemplateRules]);

  const documentTemplateOptions = useMemo(() => {
    const templateIds = (gdtDocumentTemplateRules ?? []).map(
      (rule) => rule.documentTemplateId,
    );
    const templates = (gdtTemplates ?? [])
      .filter((t) => templateIds.includes(t.id))
      .map((x) => {
        return {
          id: x.id,
          value: x.isDefault ? i18nextGlobal.t(x.title as any) : x.title,
        };
      });
    setSelectedDocumentTemplates(templates.map((t) => t.id));
    return templates;
  }, [gdtTemplates, gdtDocumentTemplateRules]);

  const documentsQuery = QueryFactory.FileQuery.useSearchFilesQuery({
    dataBlockId: blockId,
    excludeFileType: defaultExcludedFileTypes,
  });

  useEffect(() => {
    const sortedDocuments: DocumentInfo[] = (documentsQuery.data ?? [])
      .filter(
        (d) =>
          filteredFileTypes.includes(d.type) &&
          getDocumentExtension(d.name).toLowerCase() === '.pdf',
      )
      .map((document) => {
        return {
          id: document.id,
          name: document.name,
          type: document.type,
          isSelected: true,
        };
      })
      .sort((x, y) => (x.type > y.type ? 1 : -1));
    setDocuments(sortedDocuments);
  }, [documentsQuery.data]);

  const isSendEnabled =
    selectedDataBlockTemplates.length > 0 ||
    (selectedDocumentTemplates.length > 0 &&
      documents.some((d) => d.isSelected));

  const onSend = useEventCallback(async () => {
    setSending(true);
    await tryCompleteForm(formType, formId, async () => {
      try {
        if (selectedDataBlockTemplates.length > 0) {
          await QueryFactory.GDTOutgoingMessageClient.sendMeasurementMessage(
            blockId,
            {
              templateIds: selectedDataBlockTemplates,
            },
          );
        }
        if (selectedDocumentTemplates.length > 0) {
          for (const document of documents) {
            if (document.isSelected) {
              await QueryFactory.GDTOutgoingMessageClient.sendDocumentMessage(
                document.id,
                {
                  templateIds: selectedDocumentTemplates,
                },
              );
            }
          }
        }
        toast(i18nextNotifications.t('gdt_message_created'), {
          type: 'success',
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.GDTMessageQuery.getMessagesQueryKey(),
        });
        if (
          selectedDocumentTemplates.length > 0 &&
          documents.some((x) => x.isSelected)
        ) {
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
              dataBlockId: blockId,
              excludeFileType: defaultExcludedFileTypes,
            }),
          });
        }
      } catch (err) {
        await modals.showError({ text: parseError(err) });
      } finally {
        setSending(false);
      }
    });
  });

  const documentList = useMemo(
    () =>
      documents.map((document) => (
        <div
          key={document.id}
          className={styles.documentItem}
          data-test-id="document-item"
        >
          <CheckBox
            checked={document.isSelected}
            name={document.id}
            onChange={() =>
              setDocuments(
                documents.map((d) =>
                  d.id === document.id
                    ? { ...d, isSelected: !d.isSelected }
                    : d,
                ),
              )
            }
          />
          <div className={styles.documentName} data-test-id="document-name">
            {document.name}
          </div>
          <div className={styles.documentType} data-test-id="document-type">
            {i18next.t(`file_type_${document.type}`)}
          </div>
        </div>
      )),
    [documents],
  );

  return (
    <div className={styles.container}>
      <div className={styles.dataContainer}>
        <Field
          titleClassName={styles.documentsTitle}
          className={styles.field}
          title={i18next.t('select_data_block_templates')}
        >
          <CheckboxGroup
            rootClassName={styles.options}
            options={dataBlockTemplateOptions}
            valueField={'id'}
            labelField={'value'}
            selectedValues={selectedDataBlockTemplates}
            onSelectedOptionChanged={(options) =>
              setSelectedDataBlockTemplates(options.map((x) => x.id))
            }
          />
        </Field>
        {documentTemplateOptions.length > 0 && (
          <Field
            titleClassName={styles.documentsTitle}
            className={styles.field}
            title={i18next.t('select_document_templates')}
          >
            <CheckboxGroup
              rootClassName={styles.options}
              options={documentTemplateOptions}
              valueField={'id'}
              labelField={'value'}
              selectedValues={selectedDocumentTemplates}
              onSelectedOptionChanged={(options) =>
                setSelectedDocumentTemplates(options.map((x) => x.id))
              }
            />
          </Field>
        )}

        {selectedDocumentTemplates.length > 0 && (
          <div className={styles.documentsContainer}>
            <div className={styles.row}>
              <div className={styles.documentsTitle}>
                {i18next.t('documents_list_title')}
              </div>
              <div className={styles.documentsOptions}>
                <UniLink
                  title={i18next.t('option_all')}
                  onClick={() =>
                    setDocuments(
                      documents.map((d) => {
                        return { ...d, isSelected: true };
                      }),
                    )
                  }
                  className={styles.link}
                  color={ButtonColor.Secondary}
                />
                <UniLink
                  title={i18next.t('option_none')}
                  onClick={() =>
                    setDocuments(
                      documents.map((d) => {
                        return { ...d, isSelected: false };
                      }),
                    )
                  }
                  className={styles.link}
                  color={ButtonColor.Secondary}
                />
              </div>
            </div>
            <div className={styles.documentsList} data-test-id="documents-list">
              {documentList}
            </div>
          </div>
        )}
      </div>
      <div className={styles.controlsContainer}>
        <Button
          disabled={isSending || !isSendEnabled}
          title={i18next.t('send_button')}
          onClick={onSend}
        />
      </div>
    </div>
  );
};
